import React from 'react';
import { Link } from 'react-router-dom';
import { Code, Smartphone, Cloud, Database, Shield, Zap, ArrowRight, Laptop } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const ITServices = () => {
  const { language } = useLanguage();

  const services = [
    {
      icon: Laptop,
      title: {
        fr: 'Développement Web',
        en: 'Web Development',
        ar: 'تطوير الويب'
      },
      description: {
        fr: 'Sites web modernes, responsive et optimisés pour le référencement',
        en: 'Modern, responsive websites optimized for SEO',
        ar: 'مواقع ويب حديثة ومتجاوبة ومُحسَّنة لمحركات البحث'
      }
    },
    {
      icon: Smartphone,
      title: {
        fr: 'Applications Mobile',
        en: 'Mobile Applications',
        ar: 'تطبيقات الجوال'
      },
      description: {
        fr: 'Applications iOS et Android natives ou cross-platform',
        en: 'Native or cross-platform iOS and Android applications',
        ar: 'تطبيقات iOS و Android الأصلية أو متعددة المنصات'
      }
    },
    {
      icon: Cloud,
      title: {
        fr: 'Cloud & Hébergement',
        en: 'Cloud & Hosting',
        ar: 'السحابة والاستضافة'
      },
      description: {
        fr: 'Solutions cloud sécurisées avec AWS, Azure, Google Cloud',
        en: 'Secure cloud solutions with AWS, Azure, Google Cloud',
        ar: 'حلول سحابية آمنة مع AWS و Azure و Google Cloud'
      }
    },
    {
      icon: Database,
      title: {
        fr: 'Bases de données',
        en: 'Databases',
        ar: 'قواعد البيانات'
      },
      description: {
        fr: 'Conception et gestion de bases de données performantes',
        en: 'Design and management of high-performance databases',
        ar: 'تصميم وإدارة قواعد بيانات عالية الأداء'
      }
    },
    {
      icon: Shield,
      title: {
        fr: 'Sécurité',
        en: 'Security',
        ar: 'الأمان'
      },
      description: {
        fr: 'Audits de sécurité et protection des données',
        en: 'Security audits and data protection',
        ar: 'عمليات تدقيق الأمان وحماية البيانات'
      }
    },
    {
      icon: Zap,
      title: {
        fr: 'Optimisation',
        en: 'Optimization',
        ar: 'التحسين'
      },
      description: {
        fr: 'Amélioration des performances et de l\'expérience utilisateur',
        en: 'Performance improvement and user experience',
        ar: 'تحسين الأداء وتجربة المستخدم'
      }
    }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="it-services-page">
      {/* Hero Section - Couleurs maquette */}
      <section className="relative h-[600px] overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="it-hero-video"
        >
          <source
            src="https://videos.pexels.com/video-files/7147921/7147921-uhd_2560_1440_25fps.mp4"
            type="video/mp4"
          />
          <source
            src="https://cdn.pixabay.com/video/2022/01/18/105013-666386090_large.mp4"
            type="video/mp4"
          />
        </video>
        
        <div className="absolute inset-0 bg-gradient-to-r from-maquette-dark/80 to-maquette-blue/60"></div>
        
        <div className="relative z-10 container mx-auto px-4 lg:px-8 h-full flex items-center">
          <div className="max-w-3xl text-white">
            <div className="flex items-center space-x-2 mb-4">
              <Code size={32} />
              <span className="font-semibold text-lg">
                {language === 'fr' && 'Solutions IT'}
                {language === 'en' && 'IT Solutions'}
                {language === 'ar' && 'حلول تقنية المعلومات'}
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="it-services-title">
              {language === 'fr' && 'Transformation digitale & Innovation'}
              {language === 'en' && 'Digital Transformation & Innovation'}
              {language === 'ar' && 'التحول الرقمي والابتكار'}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-100">
              {language === 'fr' && 'Accompagnement complet pour digitaliser votre entreprise : développement web & mobile, cloud computing, et solutions sur mesure.'}
              {language === 'en' && 'Complete support to digitalize your business: web & mobile development, cloud computing, and custom solutions.'}
              {language === 'ar' && 'دعم كامل لرقمنة عملك: تطوير الويب والجوال، الحوسبة السحابية، والحلول المخصصة.'}
            </p>
            <Link
              to="/contact"
              data-testid="it-cta"
              className="inline-flex items-center space-x-2 bg-white text-maquette-blue px-8 py-4 rounded-md font-semibold hover:bg-gray-100 transition-all shadow-lg"
            >
              <span>
                {language === 'fr' && 'Démarrer un projet'}
                {language === 'en' && 'Start a project'}
                {language === 'ar' && 'ابدأ مشروعاً'}
              </span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid - Couleurs maquette */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-maquette-text mb-4">
              {language === 'fr' && 'Nos services IT'}
              {language === 'en' && 'Our IT services'}
              {language === 'ar' && 'خدمات تقنية المعلومات لدينا'}
            </h2>
            <p className="text-xl text-maquette-gray">
              {language === 'fr' && 'Des solutions technologiques adaptées à vos besoins'}
              {language === 'en' && 'Technology solutions tailored to your needs'}
              {language === 'ar' && 'حلول تكنولوجية مصممة خصيصاً لاحتياجاتك'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border-t-4 border-maquette-blue"
              >
                <div className="bg-maquette-blue/10 rounded-full w-16 h-16 flex items-center justify-center mb-6">
                  <service.icon className="text-maquette-blue" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-3 text-maquette-text">{service.title[language]}</h3>
                <p className="text-maquette-gray leading-relaxed">{service.description[language]}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Showcase Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-maquette-text mb-4">
              {language === 'fr' && 'Notre approche en vidéo'}
              {language === 'en' && 'Our approach in video'}
              {language === 'ar' && 'نهجنا بالفيديو'}
            </h2>
            <p className="text-xl text-maquette-gray">
              {language === 'fr' && 'Découvrez comment nous transformons vos idées en réalité'}
              {language === 'en' && 'Discover how we transform your ideas into reality'}
              {language === 'ar' && 'اكتشف كيف نحول أفكارك إلى واقع'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="rounded-xl overflow-hidden shadow-lg bg-white" data-testid="tech-video-1">
              <video
                controls
                className="w-full h-full object-cover"
                poster="https://images.unsplash.com/photo-1518770660439-4636190af475?w=800"
              >
                <source
                  src="https://cdn.pixabay.com/video/2021/09/01/88606-599081804_large.mp4"
                  type="video/mp4"
                />
              </video>
              <div className="p-4">
                <h3 className="text-xl font-bold text-maquette-text">
                  {language === 'fr' && 'Développement Logiciel'}
                  {language === 'en' && 'Software Development'}
                  {language === 'ar' && 'تطوير البرمجيات'}
                </h3>
                <p className="text-maquette-gray">
                  {language === 'fr' && 'Technologies modernes et méthodes agiles'}
                  {language === 'en' && 'Modern technologies and agile methods'}
                  {language === 'ar' && 'تقنيات حديثة وطرق رشيقة'}
                </p>
              </div>
            </div>

            <div className="rounded-xl overflow-hidden shadow-lg bg-white" data-testid="tech-video-2">
              <video
                controls
                className="w-full h-full object-cover"
                poster="https://images.unsplash.com/photo-1504639725590-34d0984388bd?w=800"
              >
                <source
                  src="https://cdn.pixabay.com/video/2020/05/26/40986-423910595_large.mp4"
                  type="video/mp4"
                />
              </video>
              <div className="p-4">
                <h3 className="text-xl font-bold text-maquette-text">
                  {language === 'fr' && 'Innovation & IA'}
                  {language === 'en' && 'Innovation & AI'}
                  {language === 'ar' && 'الابتكار والذكاء الاصطناعي'}
                </h3>
                <p className="text-maquette-gray">
                  {language === 'fr' && 'Intelligence artificielle et automatisation'}
                  {language === 'en' && 'Artificial intelligence and automation'}
                  {language === 'ar' && 'الذكاء الاصطناعي والأتمتة'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-maquette-text mb-4">
              {language === 'fr' && 'Technologies maîtrisées'}
              {language === 'en' && 'Mastered technologies'}
              {language === 'ar' && 'تقنيات متقنة'}
            </h2>
            <p className="text-xl text-maquette-gray">
              {language === 'fr' && 'Stack technologique moderne et performant'}
              {language === 'en' && 'Modern and efficient technology stack'}
              {language === 'ar' && 'حزمة تقنية حديثة وفعالة'}
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {['React', 'Node.js', 'Python', 'MongoDB', 'AWS', 'Docker', 'Next.js', 'TypeScript'].map((tech, index) => (
                <div
                  key={index}
                  className="bg-white border-2 border-maquette-blue/20 p-6 rounded-lg text-center font-semibold text-maquette-text hover:border-maquette-blue hover:shadow-lg transition-all"
                >
                  {tech}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Couleurs maquette */}
      <section className="py-20 bg-gradient-to-r from-maquette-blue to-maquette-dark text-white">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            {language === 'fr' && 'Prêt à digitaliser votre entreprise ?'}
            {language === 'en' && 'Ready to digitalize your business?'}
            {language === 'ar' && 'هل أنت مستعد لرقمنة عملك؟'}
          </h2>
          <p className="text-xl mb-8 text-gray-100">
            {language === 'fr' && 'Discutons de votre projet et trouvons ensemble la meilleure solution'}
            {language === 'en' && 'Let\'s discuss your project and find the best solution together'}
            {language === 'ar' && 'دعنا نناقش مشروعك ونجد أفضل حل معاً'}
          </p>
          <Link
            to="/contact"
            data-testid="it-contact-cta"
            className="inline-flex items-center space-x-2 bg-white text-maquette-blue px-8 py-4 rounded-md font-semibold hover:bg-gray-100 transition-all shadow-lg"
          >
            <span>
              {language === 'fr' && 'Demander une consultation'}
              {language === 'en' && 'Request a consultation'}
              {language === 'ar' && 'طلب استشارة'}
            </span>
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ITServices;
